import { z } from 'zod'

import { UserInfoSchema } from '@/entities/chat'
import { parseJSONDate } from '@/shared/lib/date-fns/parse-json-date'

// === ChannelEvent ===

export const MessageSchema = z.object({
    id: z.string(),
    user_id: z.string(),
    data: z.string(),
    create_time: z.union([z.string().transform(parseJSONDate), z.date()]),
    reply_message_id: z.string().optional(),
    reply_user_id: z.string().optional(),
    optimistic_id: z.string().optional(),
})

export type Message = z.infer<typeof MessageSchema>

export const MessageHiddenSchema = z.object({
    message_id: z.string(),
})

export const MessageHiddenManySchema = z.object({
    message_id_list: z.array(z.string()),
})

export const MessageHiddenAllSchema = z.object({
    user_id: z.string(),
})

export const ChannelSlowmodeSchema = z.object({
    channel: z.string(),
    cooldown_seconds: z.number().nullable(),
})

export const MessageWelcomeSchema = z.object({
    message: z.string(),
})

export type ChannelSlowmode = z.infer<typeof ChannelSlowmodeSchema>

export const MessageReactionSchema = z.object({
    message_id: z.string(),
    user_id: z.string(),
    emoji: z.string(),
})

export const MessageTranslateRequestSchema = z.object({
    message_id_list: z.array(z.string()),
    target_lang: z.string(),
})

export type MessageTranslateRequest = z.infer<typeof MessageTranslateRequestSchema>

export const MessageTranslationSchema = z.object({
    message_id: z.string(),
    translated_text: z.string(),
    detected_language_code: z.string(),
})

export type MessageTranslation = z.infer<typeof MessageTranslationSchema>

export const MessageTranslateResponseSchema = z.object({
    translation: z.record(z.string(), MessageTranslationSchema),
})

export type MessageTranslateResponse = z.infer<typeof MessageTranslateResponseSchema>

// ChannelEvent

export const ChannelEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('message_published'), payload: MessageSchema }),
    z.object({ type: z.literal('message_hidden'), payload: MessageHiddenSchema }),
    z.object({ type: z.literal('message_hidden_many'), payload: MessageHiddenManySchema }),
    z.object({ type: z.literal('message_reaction_added'), payload: MessageReactionSchema }),
    z.object({ type: z.literal('message_reaction_removed'), payload: MessageReactionSchema }),
])

export type ChannelEvent = z.infer<typeof ChannelEventSchema>

// UserMute

export const UserMuteSchema = z.object({
    user_id: z.string(),
    reason_code: z.string(),
    create_time: z.string().transform(parseJSONDate),
    update_time: z.string().transform(parseJSONDate),
    expire_time: z.string().transform(parseJSONDate).nullable(),
})

export type UserMute = z.infer<typeof UserMuteSchema>

// ChannelAdmin

export const ChatAdminSchema = z.object({
    user_id: z.string(),
    role: z.union([z.literal('admin'), z.literal('moderator')]).optional(),
})

export type ChatAdmin = z.infer<typeof ChatAdminSchema>

// Slowmode

export const UserSlowmodeConfigSchema = z.object({
    channel: z.string(),
    cooldown_seconds: z.number(),
    cooldown_left: z.number().nullable(),
})

// Chat settings

export const MaintenanceSettingSchema = z.object({
    enabled: z.boolean(),
})

export const MessageLimitSettingSchema = z.object({
    user: z.number(),
    admin: z.number(),
})

export const ChatSettingsSchema = z.object({
    maintenance: MaintenanceSettingSchema,
    message_limit: MessageLimitSettingSchema,
})

export const ChatChannelSchema = z.object({
    name: z.string(),
})

export type MaintenanceSetting = z.infer<typeof MaintenanceSettingSchema>
export type MessageLimitSetting = z.infer<typeof MessageLimitSettingSchema>
export type ChatSettings = z.infer<typeof ChatSettingsSchema>
export type ChatChannel = z.infer<typeof ChatChannelSchema>

// Login

export const LoginResponseSchema = z.object({
    user: UserInfoSchema,
    channels: z.array(ChatChannelSchema),
    slowmode: z.record(z.string(), UserSlowmodeConfigSchema).optional(),
    chat_admins: z
        .array(ChatAdminSchema)
        .optional()
        .transform((members) => members ?? []),
    user_mutes: z
        .array(UserMuteSchema)
        .optional()
        .transform((mutes) => mutes ?? []),
    chat_settings: ChatSettingsSchema,
})

export type LoginRequest = {
    lang: string
}

export type LoginResponse = z.infer<typeof LoginResponseSchema>

// === SystemEvent ===

export const UserMutedSchema = UserMuteSchema
export const UserUnmutedSchema = z.object({
    user_id: z.string(),
    reason_message: z.string(),
})

export const SystemEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('user_muted'), payload: UserMutedSchema }),
    z.object({ type: z.literal('user_unmuted'), payload: UserUnmutedSchema }),
    z.object({ type: z.literal('message_hidden_all'), payload: MessageHiddenAllSchema }),
    z.object({ type: z.literal('channel_slowmode'), payload: ChannelSlowmodeSchema }),
    z.object({ type: z.literal('chat_settings_updated'), payload: ChatSettingsSchema }),
])

export type SystemEvent = z.infer<typeof SystemEventSchema>

// Utils

export type ChannelEventOfType<T extends ChannelEvent['type']> = Extract<ChannelEvent, { type: T }>

export function channelEventOfType<T extends ChannelEvent['type'], TEvent extends ChannelEventOfType<T>>(
    type: T
): (event: ChannelEvent) => event is TEvent {
    return (event): event is TEvent => event.type === type
}

export function isChannelEventOfType<T extends ChannelEvent['type'], TEvent extends Extract<ChannelEvent, { type: T }>>(
    type: T,
    event: ChannelEvent
): event is TEvent {
    return event.type === type
}

// History

export const HistoryResponseSchema = z.object({
    messages: z.array(MessageSchema),
    // message_id ->
    reaction_by_message_id: z.record(
        z.string(),
        // emoji -> user_id[]
        z.record(z.string(), z.array(z.string()))
    ),
})

export type HistoryResponse = z.infer<typeof HistoryResponseSchema>
